import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiExternalLink, FiGithub, FiPlay } from 'react-icons/fi';

const projects = [
  {
    title: 'RutX — SFA App',
    subtitle: 'Sales Force Automation Mobile App',
    company: 'Appstrail Technologies',
    period: '2025 — Present',
    tags: ['React Native', 'REST APIs', 'Salesforce'],
    status: 'live',
    statusLabel: 'Live on Play Store',
    color: 'primary',
    description: 'Production-grade Sales Force Automation mobile application for Appstrail Technologies. Enables real-time field sales tracking, order management, and reporting for enterprise clients including Raymond, Nuzivedu Seeds, Priya Cement, and VNR Seeds.',
    link: null,
    highlights: ['Google Play Store', 'Raymond, VNR Seeds clients', 'Salesforce-integrated backend'],
  },
  {
    title: 'SDK App — SFA Mobile SDK',
    subtitle: 'Reusable React Native SDK',
    company: 'Appstrail Technologies',
    period: '2025 — Present',
    tags: ['React Native', 'Salesforce', 'REST APIs'],
    status: 'live',
    statusLabel: 'Enterprise SDK',
    color: 'primary',
    description: 'Engineered a reusable React Native SDK companion for the RutX SFA platform, enabling modular Salesforce integration across multiple enterprise client deployments with minimal configuration.',
    link: null,
    highlights: ['Modular architecture', 'Multi-client deployment', 'Enterprise-grade'],
  },
  {
    title: 'Samai App',
    subtitle: 'GenAI Android Application',
    company: 'Scontinent Technologies',
    period: '2025',
    tags: ['Python', 'Generative AI', 'REST APIs', 'Android'],
    status: 'live',
    statusLabel: 'Live on Play Store',
    color: 'accent',
    description: 'AI-powered Android application by Scontinent Technologies with an active user base on the Google Play Store. Built Python-based Generative AI backend supporting real-time AI processing, automated workflows, and intelligent user interaction via scalable REST API endpoints.',
    link: null,
    highlights: ['Active user base', 'Real-time AI processing', 'Automated workflows'],
  },
  {
    title: 'NammaCET',
    subtitle: 'KCET Rank Predictor Platform',
    company: 'Scontinent Technologies',
    period: '2025',
    tags: ['Python', 'ML', 'REST APIs', 'React.js'],
    status: 'live',
    statusLabel: 'Live Website',
    color: 'blue',
    description: 'Architected and deployed nammacet.com — a machine learning platform for accurate KCET rank prediction and college recommendation. Serves 10,000+ Karnataka CET aspirants. Deployed on cloud infrastructure with automated Shell script CI/CD pipelines.',
    link: 'https://nammacet.com',
    highlights: ['10,000+ students served', 'ML rank prediction', 'Live Web APIs'],
  },
  {
    title: 'ConnectStudents',
    subtitle: 'Academic Networking Platform',
    company: 'Independent / Product for Society',
    period: '2024',
    tags: ['Full Stack', 'Web Platform', 'Node.js'],
    status: 'live',
    statusLabel: 'Product for Society',
    color: 'violet',
    description: 'Built and launched ConnectStudents — a social and academic networking platform recognized as a Product for Society for its contribution to student community engagement. Connects students across institutions enabling resource sharing, peer networking, and academic collaboration.',
    link: 'https://connectstudents.in/',
    highlights: ['Product for Society award', 'Cross-institution networking', 'Scalable architecture'],
  },
  {
    title: 'DQEH — DigiRaksha',
    subtitle: 'Quantum Encryption Hasher Python SDK',
    company: 'Independent',
    period: '2024',
    tags: ['Python', 'Cybersecurity', 'PyPI', 'npm'],
    status: 'published',
    statusLabel: 'PyPI + npm Published',
    color: 'rose',
    description: 'Developed and published a Python SDK implementing advanced encryption and hashing techniques for cybersecurity applications. Available as both a PyPI library and npm package, enabling cross-platform integration for developers.',
    link: null,
    highlights: ['PyPI published', 'npm published', 'Advanced encryption'],
  },
];

const colorConfig = {
  primary: {
    bg: 'bg-primary-50',
    text: 'text-primary-600',
    badge: 'bg-primary-100 text-primary-700',
    border: 'border-primary-200',
    dot: 'bg-primary-500',
    btn: 'bg-primary-600 hover:bg-primary-700 text-white',
    tag: 'bg-primary-50 text-primary-700 border-primary-100',
  },
  accent: {
    bg: 'bg-accent-50',
    text: 'text-accent-600',
    badge: 'bg-accent-100 text-accent-700',
    border: 'border-accent-200',
    dot: 'bg-accent-500',
    btn: 'bg-accent-600 hover:bg-accent-700 text-white',
    tag: 'bg-accent-50 text-accent-700 border-accent-100',
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
    btn: 'bg-blue-600 hover:bg-blue-700 text-white',
    tag: 'bg-blue-50 text-blue-700 border-blue-100',
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-600',
    badge: 'bg-violet-100 text-violet-700',
    border: 'border-violet-200',
    dot: 'bg-violet-500',
    btn: 'bg-violet-600 hover:bg-violet-700 text-white',
    tag: 'bg-violet-50 text-violet-700 border-violet-100',
  },
  rose: {
    bg: 'bg-rose-50',
    text: 'text-rose-600',
    badge: 'bg-rose-100 text-rose-700',
    border: 'border-rose-200',
    dot: 'bg-rose-500',
    btn: 'bg-rose-600 hover:bg-rose-700 text-white',
    tag: 'bg-rose-50 text-rose-700 border-rose-100',
  },
};

function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const cfg = colorConfig[project.color];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.06 }}
      className="card flex flex-col overflow-hidden group"
    >
      {/* Card Top Band */}
      <div className={`h-1.5 w-full ${cfg.dot}`} />

      <div className="p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-bold text-slate-900 text-base">{project.title}</h3>
              <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} animate-pulse`} />
                {project.statusLabel}
              </span>
            </div>
            <p className={`text-xs font-medium mt-0.5 ${cfg.text}`}>{project.subtitle}</p>
          </div>
        </div>

        <p className="text-xs text-slate-500 mb-1 font-medium">{project.company} · {project.period}</p>

        <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">{project.description}</p>

        {/* Highlights */}
        <div className="space-y-1.5 mb-4">
          {project.highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
              {h}
            </div>
          ))}
        </div>

        {/* Tech Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {project.tags.map((tag) => (
            <span key={tag} className={`tag border ${cfg.tag}`}>{tag}</span>
          ))}
        </div>

        {/* CTA */}
        {project.link && (
          <a
            href={project.link}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-all duration-200 active:scale-95 ${cfg.btn}`}
          >
            <FiExternalLink size={13} />
            Visit Project
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="projects" className="bg-slate-50">
      <div className="section-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-1 h-8 bg-gradient-to-b from-primary-600 to-accent-500 rounded-full" />
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Key Projects</p>
          </div>
          <h2 className="section-title">Work That Ships</h2>
          <p className="text-slate-400 text-sm">Production applications and platforms used by real people.</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((project, i) => (
            <ProjectCard key={project.title} project={project} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
